import { useState, useEffect, useMemo } from 'react';
import { Download } from 'lucide-react';

const PWA_PROMPT_EXCLUDED_PATHS = new Set(['/clases', '/classes']);

function isPromptExcludedPath() {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  return PWA_PROMPT_EXCLUDED_PATHS.has(pathname)
    || /^\/free-conversations\/(?:es|en|no)\/signup$/i.test(pathname)
    || /^\/(?:es|en|no)\/live-classes\/signup$/i.test(pathname);
}

const isSafariIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream && !/CriOS|FxiOS/.test(ua);
};

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isExcludedPath, setIsExcludedPath] = useState(() => isPromptExcludedPath());
  const safari = useMemo(() => isSafariIOS(), []);

  useEffect(() => {
    const syncPathState = () => {
      const nextIsExcluded = isPromptExcludedPath();
      setIsExcludedPath(nextIsExcluded);

      if (nextIsExcluded) {
        setShowPrompt(false);
      }
    };

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function pushState(...args) {
      const result = originalPushState.apply(this, args);
      window.dispatchEvent(new Event('aprendos:navigation'));
      return result;
    };

    window.history.replaceState = function replaceState(...args) {
      const result = originalReplaceState.apply(this, args);
      window.dispatchEvent(new Event('aprendos:navigation'));
      return result;
    };

    window.addEventListener('popstate', syncPathState);
    window.addEventListener('aprendos:navigation', syncPathState);
    syncPathState();

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', syncPathState);
      window.removeEventListener('aprendos:navigation', syncPathState);
    };
  }, []);

  useEffect(() => {
    if (isExcludedPath) {
      setShowPrompt(false);
      return undefined;
    }

    const isStandaloneMode = window.navigator.standalone ||
      window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(isStandaloneMode);

    const handleBeforeInstallPrompt = (e) => {
      if (isPromptExcludedPath()) return;
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    const timer = setTimeout(() => {
      if (!isStandaloneMode && !localStorage.getItem('pwa-prompt-dismissed')) {
        setShowPrompt(true);
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(timer);
    };
  }, [isExcludedPath]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`PWA install outcome: ${outcome}`);
      setDeferredPrompt(null);
    }
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  if (isExcludedPath || isStandalone || !showPrompt) return null;

  // Safari iOS: show step-by-step instructions
  if (safari) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 sm:left-6 sm:right-6 md:max-w-sm md:left-auto md:right-6">
        <div className="bg-white border border-[#e7e5e4] rounded-2xl shadow-[0_24px_60px_-40px_rgba(28,25,23,.25)] p-5">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FAF7F2] border border-[#e7e5e4] flex items-center justify-center">
              <div className="w-3 h-3 rounded-full" style={{ background: 'linear-gradient(135deg, #D63AB4, #E38ECF)' }} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-[#1c1917] text-sm">Add Aprendos to Home Screen</h3>
              <p className="text-[#57534e] text-xs mt-1 leading-relaxed">Learn offline and open lessons faster.</p>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-[#e7e5e4] space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-[#FAF7F2] border border-[#e7e5e4] flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-[#1c1917]">1</span>
              </div>
              <p className="text-xs text-[#57534e]">
                Tap the{' '}
                <strong className="text-[#1c1917]">Share</strong>{' '}
                button{' '}
                <svg className="inline-block ml-0.5 -mt-0.5" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D63AB4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" /></svg>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-[#FAF7F2] border border-[#e7e5e4] flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-[#1c1917]">2</span>
              </div>
              <p className="text-xs text-[#57534e]">
                Scroll down and tap <strong className="text-[#1c1917]">Add to Home Screen</strong>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-[#FAF7F2] border border-[#e7e5e4] flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-[#1c1917]">3</span>
              </div>
              <p className="text-xs text-[#57534e]">
                Tap <strong className="text-[#1c1917]">Add</strong>
              </p>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={handleDismiss}
              className="text-[#78716c] text-xs font-medium hover:text-[#1c1917] transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Chrome / Edge / Desktop: native install prompt
  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 sm:left-6 sm:right-6 md:max-w-sm md:left-auto md:right-6">
      <div className="bg-white border border-[#e7e5e4] rounded-2xl shadow-[0_24px_60px_-40px_rgba(28,25,23,.25)] p-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FAF7F2] border border-[#e7e5e4] flex items-center justify-center">
            <div className="w-3 h-3 rounded-full" style={{ background: 'linear-gradient(135deg, #D63AB4, #E38ECF)' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#1c1917] text-sm">Install Aprendos</p>
            <p className="text-[#57534e] text-xs mt-1 leading-relaxed">Learn offline, get notifications, and open lessons faster.</p>
            <div className="flex items-center gap-3 mt-4">
              <button
                type="button"
                onClick={handleInstall}
                className="inline-flex items-center gap-1.5 bg-[#1c1917] text-white text-xs font-semibold px-4 py-2 rounded-full hover:bg-[#292524] transition-colors"
              >
                <Download size={12} />
                Install
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="text-[#78716c] text-xs font-medium hover:text-[#1c1917] transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
